import { useEffect, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import Card from '@/components/Card';
import { Input } from '@/components/Controls/Input';
import { Datatable } from '@/components/Datatable/Datatable';
import { TableViewProps } from '@/type/listing.types';
import { TableTitle } from '@/components/Header/TableTitle';
import Loader from '@/components/Loading/Loader';
import { ClearButton } from '@/components/Custom/Button/ClearButton';
import { useHistoryFilter } from '@/hooks/filters/history-filter.hooks';
import { HistoryFilter } from '@/components/Custom/Filter/HistoryFilter';
import { HistoryType } from '@/entities/history';
import { useListAllGroups } from '@/hooks/group.hooks';
import { useListAllCompanies } from '@/hooks/company.hooks';
import { useListAllBranches } from '@/hooks/branch.hooks';
import { useListAllClients } from '@/hooks/user.hooks';
import { mapFilterSelect } from '@/utils/map.utils';
import { getOptionsVehiculeCheckType } from '@/utils/constant.utils';
import { DateTimePicker } from '@/components/Custom/DateTimePicker';
import dayjs from 'dayjs';
import { twMerge } from 'tailwind-merge';
import { isWindows } from '@/utils/os.utils';

const limit = 20;

export const EndpointHistoryTableView = <TSchema extends FieldValues>({
  t,
  useGetAllData,
  onGetTotalActive,
  tableHeaders: TableHeaders,
  tableData: TableData,
  pendingDeactivate,
  onDeactivate,
}: TableViewProps<TSchema>) => {
  const [totalPages, setTotalPages] = useState<number>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  /**
   * === Filter Section ===
   */
  const {
    debouncedFilter,
    setDebouncedFilter,
    filtersEmpty,
    clearFilters,
    key,
    startDate,
    endDate,
    groupId,
    companyId,
    branchId,
    clientType,
    clientFullName,
    searchByKey,
    searchStartDate,
    searchEndDate,
    searchByGroup,
    searchByCompany,
    searchByBranch,
    searchByClientType,
    searchByClient,
  } = useHistoryFilter({
    historyType: HistoryType.ENDPOINT_HISTORY,
    setCurrentPage,
  });

  useEffect(() => {
    setDebouncedFilter((prev) => ({
      ...prev,
      groupId,
      companyId,
      branchId,
      clientType,
      clientFullName,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, companyId, branchId, clientType, clientFullName]);
  /**
   * === END OF Filter Section ===
   */

  /**
   * === Data Fetching section ===
   */
  const {
    data: allData,
    isLoading: isLoadingAllData,
    isPending: isPendingAllData,
  } = useGetAllData(
    debouncedFilter,
    currentPage,
    limit,
    sortColumn,
    sortDirection,
  );

  const { data: allGroups, isLoading: isLoadingAllGroups } = useListAllGroups();
  const { data: allCompanies, isLoading: isLoadingAllCompanies } =
    useListAllCompanies({
      groupId: debouncedFilter?.groupId,
    });
  const { data: allBranches, isLoading: isLoadingAllBranches } =
    useListAllBranches({
      groupId: debouncedFilter?.groupId,
      companyId: debouncedFilter?.companyId,
    });
  const { data: allClients, isLoading: isLoadingAllClients } =
    useListAllClients({
      groupId: debouncedFilter?.groupId,
      companyId: debouncedFilter?.companyId,
      branchId: debouncedFilter?.branchId,
      type: debouncedFilter?.clientType,
    });
  /**
   * === END OF Data Fetching section ===
   */

  /**
   * === Pagination Section ===
   */
  useEffect(() => {
    setTotalPages(allData?.data?.totalPages);
    onGetTotalActive?.(allData?.data?.['total']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allData]);
  /**
   * === END OF Pagination Section ===
   */

  /**
   * === Sorting Section ===
   */
  const handleSort = (column: string) => {
    setSortColumn(column);
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };
  /**
   * === END OF Sorting Section ===
   */

  return (
    <>
      <Card bodyClassName="flex flex-col gap-5">
        <div className="flex flex-wrap justify-between gap-3.5">
          <div className="flex gap-3.5 max-1.5xl:w-full max-1.5xl:justify-between">
            <TableTitle textClassName="mt-1.5" text={t('common:endpoints')} />
            <div className="hidden max-md:block">
              <ClearButton disabled={filtersEmpty} onClick={clearFilters} />
            </div>
          </div>
          <div className="flex flex-col gap-3 max-[1499px]:w-full">
            <div className="flex flex-wrap-reverse justify-end items-center gap-2.5">
              <div className="flex flex-wrap items-center gap-2.5 max-[1499px]:w-full">
                <Input
                  placeholder={t('common:searchLicensePlateOrVin')}
                  type="text"
                  name="search"
                  autoComplete="off"
                  leftIcon={
                    <i className="ki-outline ki-magnifier text-lg text-gray-8990A8" />
                  }
                  inputWrapperClassName={twMerge(
                    'w-full sm:w-auto sm:min-w-[332px]',
                    isWindows && 'sm:min-w-[380px]',
                  )}
                  inputClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                  value={key}
                  onChange={(e) => searchByKey(e.target.value)}
                />
                <div className="flex justify-center gap-3 bg-purple-9D8AFF/5 px-3.5 py-1.5 rounded-lg select-datepicker-container max-[1292px]:w-full max-sm:flex-col">
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 max-sm:w-full">
                    <label className="text-gray-600 text-2sm self-center">
                      {t('common:createdBetween_feminine')}
                    </label>
                    <div className="max-sm:w-full">
                      <DateTimePicker
                        useTime={false}
                        value={startDate ? dayjs(startDate) : null}
                        onChange={searchStartDate}
                        disablePast={false}
                        numberOfAllowedFutureDays={0}
                        maxDate={endDate ? dayjs(endDate) : null}
                        textFieldSx={{
                          width: { xs: '100%', sm: 'auto' },
                          maxWidth: { xs: 'none', sm: 150 },
                        }}
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 max-sm:w-full">
                    <label className="text-gray-600 text-2sm self-center">
                      {t('common:and').toLowerCase()}
                    </label>
                    <div className="max-sm:w-full">
                      <DateTimePicker
                        useTime={false}
                        value={endDate ? dayjs(endDate) : null}
                        onChange={searchEndDate}
                        disablePast={false}
                        numberOfAllowedFutureDays={0}
                        minDate={startDate ? dayjs(startDate) : null}
                        textFieldSx={{
                          width: { xs: '100%', sm: 'auto' },
                          maxWidth: { xs: 'none', sm: 150 },
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className="max-[1499px]:-mt-[48px] max-md:hidden">
                  <ClearButton disabled={filtersEmpty} onClick={clearFilters} />
                </div>
              </div>
            </div>
            <div>
              <HistoryFilter
                t={t}
                isLoadingAllGroups={isLoadingAllGroups}
                isLoadingAllCompanies={isLoadingAllCompanies}
                isLoadingAllBranches={isLoadingAllBranches}
                isLoadingAllClients={isLoadingAllClients}
                groupOptions={mapFilterSelect(allGroups?.data)}
                companyOptions={mapFilterSelect(allCompanies?.data)}
                branchOptions={mapFilterSelect(allBranches?.data)}
                clientTypeOptions={getOptionsVehiculeCheckType(t)}
                clientOptions={mapFilterSelect(allClients?.data)}
                groupId={groupId}
                companyId={companyId}
                branchId={branchId}
                clientType={clientType}
                clientFullName={clientFullName}
                searchByGroup={searchByGroup}
                searchByCompany={searchByCompany}
                searchByBranch={searchByBranch}
                searchByClientType={searchByClientType}
                searchByClient={searchByClient}
              />
            </div>
          </div>
        </div>
        <div>
          <Datatable
            t={t}
            headers={
              <TableHeaders
                t={t}
                onSort={handleSort}
                sortDirection={sortDirection}
              />
            }
            data={
              isLoadingAllData ? (
                <></>
              ) : !allData?.data?.data?.length ? null : (
                <TableData
                  t={t}
                  data={allData?.data?.data}
                  pendingDeactivate={pendingDeactivate}
                  onDeactivate={onDeactivate}
                />
              )
            }
            canSelectItems={false}
            showPagination={allData?.data?.data?.length > 0 && totalPages > 1}
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      </Card>
      {(isLoadingAllData || isPendingAllData || pendingDeactivate) && (
        <Loader />
      )}
    </>
  );
};
